import React from "react";

// Phase 3D.5-C - Presentational card for the strictly SIMULATED, NON-OBSERVED,
// NON-PREDICTIVE campaign scenario outcomes produced by the canonical engine
// buildSimulatedCampaignOutcomes(campaignResult) (see savoieCampaignOutcomes.js).
//
// PURE PRESENTATION ONLY:
//   * Renders EXCLUSIVELY the fields already present in outcomesResult.
//   * NO analytics engine call, NO metric recompute, NO API/network, NO env
//     read, NO storage, NO mutation, NO fallback fabrication.
//   * NO hardcoded market / budget / result / period.
//   * Fail-closed guards: renders NOTHING unless outcomesResult is an explicit,
//     disabled, simulated, non-observed, non-predictive scenario result.
//   * NO button / link / form / editable field / launch|publish|activate|connect
//     control, NO ad-network brand, NO predictive chart, NO confidence gauge,
//     NO probability, NO performance gauge, NO ROI, NO real-result comparison.
//   * Never presents a value as real, observed, expected, probable, predicted
//     or guaranteed.

import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import { MISSING, periodLabel } from "@/lib/savoieKpis";

// --- Visual-only formatters (fr-FR). Never used for any calculation. ------
const nf = new Intl.NumberFormat("fr-FR");
const pf1 = new Intl.NumberFormat("fr-FR", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const fmtInt = (v) => (isNum(v) ? nf.format(Math.round(v)) : MISSING);
const fmtNum = (v) => (isNum(v) ? nf.format(v) : MISSING);
const fmtMoney = (v, c) => (isNum(v) ? `${nf.format(Math.round(v))} ${c || ""}`.trim() : MISSING);
const fmtRatioPct = (v) => (isNum(v) ? pf1.format(v) : MISSING); // 0.009 -> "0,9 %"
const fmtPct1 = (v) =>
  isNum(v) ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %` : MISSING;
const fmtPctSigned = (v) =>
  isNum(v)
    ? `${v >= 0 ? "+" : ""}${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %`
    : MISSING;

// "2025-12" -> "décembre 2025" (reuses the pure label helper; no recompute).
function frPeriod(iso) {
  if (typeof iso !== "string") return MISSING;
  const [y, m] = iso.split("-").map((x) => Number(x));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return MISSING;
  return periodLabel({ year: y, month: m });
}

// Meta must mirror the SIMULATED demo contract (no transformation, no heuristic).
function demoMetaOk(meta) {
  return (
    meta &&
    typeof meta === "object" &&
    !Array.isArray(meta) &&
    meta.is_demo === true &&
    meta.provenance === "synthetic_demo" &&
    meta.provenance_label === "DONNÉES DE DÉMONSTRATION" &&
    meta.affiliation === "none" &&
    meta.simulation === true &&
    meta.simulation_label === "SIMULATION"
  );
}

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

// Small inline "hypothesis" tag (not colour-only: carries explicit text).
function AssumptionTag() {
  return (
    <span
      className="inline-flex items-center rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700"
      data-testid="outcomes-assumption-tag"
    >
      Hypothèse
    </span>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-slate-100 py-1.5 first:border-t-0">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

// Verbatim helper notes kept as JS strings (avoid JSX unescaped-entity issues).
const EVIDENCE_NOTE =
  "Preuves issues de l'historique synthétique de démonstration — elles ne sont pas recalculées ici.";
const ASSUMPTIONS_NOTE =
  "Hypothèses fictives du scénario — ni observations réelles, ni prédictions.";
const SCENARIO_NOTE =
  "Valeurs entièrement simulées, non mesurées et non prédictives.";

export default function CampaignOutcomesCard({ outcomesResult }) {
  const r = outcomesResult;

  // --- Fail-closed display gate: render nothing unless fully conform. -----
  if (!isObj(r)) return null;
  if (r.status !== "scenario") return null;
  if (r.simulation !== true) return null;
  if (r.observed !== false) return null;
  if (r.predictive !== false) return null;
  if (r.not_a_forecast !== true) return null;
  if (r.activation_status !== "disabled") return null;
  if (r.publish_status !== "disabled") return null;
  if (r.external_campaign_id !== null) return null;
  if (r.provenance !== "synthetic_demo") return null;
  if (!isObj(r.scenario_outcomes)) return null;
  if (!isObj(r.scenario_outcomes_display)) return null;
  if (!isObj(r.evidence)) return null;
  if (!isObj(r.assumptions)) return null;
  if (!demoMetaOk(r.meta)) return null;
  if (typeof r.disclaimer !== "string" || r.disclaimer.length === 0) return null;

  const ev = r.evidence;
  const a = r.assumptions;
  const d = r.scenario_outcomes_display;

  const budget = isObj(a.budget) ? a.budget : {};
  const cpm = isObj(a.synthetic_cpm_eur) ? a.synthetic_cpm_eur : {};
  const freq = isObj(a.synthetic_avg_frequency) ? a.synthetic_avg_frequency : {};
  const ctr = isObj(a.synthetic_ctr) ? a.synthetic_ctr : {};
  const cvr = isObj(a.synthetic_conversion_rate) ? a.synthetic_conversion_rate : {};

  return (
    <section
      className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5 shadow-sm"
      aria-labelledby="campaign-outcomes-title"
      data-testid="campaign-outcomes-card"
    >
      {/* Title + explicit badges (Simulation + Données de démonstration). */}
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id="campaign-outcomes-title"
          className="font-heading text-lg font-bold tracking-tight text-indigo-900 sm:text-xl"
          data-testid="campaign-outcomes-title"
        >
          Résultats simulés du scénario
        </h2>
        <ProvenanceBadges meta={r.meta} />
      </div>

      {/* Explicit transparency labels (text, not colour-only). */}
      <div className="mt-3 flex flex-wrap gap-2">
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="campaign-outcomes-non-observed"
        >
          Résultats non observés
        </span>
        <span
          className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-slate-700"
          data-testid="campaign-outcomes-non-predictive"
        >
          Scénario non prédictif
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* A. Synthetic analytical evidence (from outcomesResult.evidence). */}
        <div
          className="rounded-xl border border-indigo-200 bg-white p-4"
          data-testid="campaign-outcomes-evidence"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
            Preuves analytiques synthétiques
          </h3>
          <dl>
            <Row label="Période analysée" value={frPeriod(ev.evidence_period)} testid="campaign-outcomes-ev-period" />
            <Row label="Période de comparaison" value={frPeriod(ev.comparison_period)} testid="campaign-outcomes-ev-comparison" />
            <Row label="Croissance des arrivées" value={fmtPctSigned(ev.growth_percent)} testid="campaign-outcomes-ev-growth" />
            <Row label="Arrivées visiteurs" value={fmtInt(ev.visitor_arrivals)} testid="campaign-outcomes-ev-arrivals" />
            <Row label="Part de marché" value={fmtPct1(ev.market_share_percent)} testid="campaign-outcomes-ev-share" />
          </dl>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="campaign-outcomes-evidence-note">
            {EVIDENCE_NOTE}
          </p>
        </div>

        {/* B. Fictional scenario assumptions (from outcomesResult.assumptions). */}
        <div
          className="rounded-xl border border-violet-200 bg-white p-4"
          data-testid="campaign-outcomes-assumptions"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Hypothèses fictives
          </h3>
          <ul className="space-y-1.5">
            <li className="flex items-center gap-2 text-sm text-slate-800" data-testid="campaign-outcomes-assumption-budget">
              <AssumptionTag />
              <span><span className="font-semibold">Budget fictif : </span>{fmtMoney(budget.amount, budget.currency)}</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-slate-800" data-testid="campaign-outcomes-assumption-cpm">
              <AssumptionTag />
              <span><span className="font-semibold">CPM synthétique : </span>{fmtMoney(cpm.value, "EUR")}</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-slate-800" data-testid="campaign-outcomes-assumption-frequency">
              <AssumptionTag />
              <span><span className="font-semibold">Fréquence moyenne synthétique : </span>{fmtNum(freq.value)}</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-slate-800" data-testid="campaign-outcomes-assumption-ctr">
              <AssumptionTag />
              <span><span className="font-semibold">CTR synthétique : </span>{fmtRatioPct(ctr.value)}</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-slate-800" data-testid="campaign-outcomes-assumption-conversion">
              <AssumptionTag />
              <span><span className="font-semibold">Taux de conversion synthétique : </span>{fmtRatioPct(cvr.value)}</span>
            </li>
          </ul>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="campaign-outcomes-assumptions-note">
            {ASSUMPTIONS_NOTE}
          </p>
        </div>
      </div>

      {/* C. Simulated mathematical scenario outcomes (display values only). */}
      <div
        className="mt-4 rounded-xl border border-slate-200 bg-white p-4"
        data-testid="campaign-outcomes-scenario-results"
      >
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Résultats mathématiques du scénario
        </h3>
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="text-xs text-slate-500">Impressions — scénario</dt>
            <dd className="text-lg font-bold text-slate-900" data-testid="campaign-outcomes-metric-impressions">
              {fmtInt(d.scenario_impressions)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Couverture — scénario</dt>
            <dd className="text-lg font-bold text-slate-900" data-testid="campaign-outcomes-metric-reach">
              {fmtInt(d.scenario_reach)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Clics — scénario</dt>
            <dd className="text-lg font-bold text-slate-900" data-testid="campaign-outcomes-metric-clicks">
              {fmtInt(d.scenario_clicks)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Conversions — scénario</dt>
            <dd className="text-lg font-bold text-slate-900" data-testid="campaign-outcomes-metric-conversions">
              {fmtInt(d.scenario_conversions)}
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs italic text-slate-500" data-testid="campaign-outcomes-scenario-note">
          {SCENARIO_NOTE}
        </p>
      </div>

      {/* Exact disclaimer (verbatim from the engine, single occurrence). */}
      <p className="mt-4 text-xs text-slate-500" data-testid="campaign-outcomes-disclaimer">
        {r.disclaimer}
      </p>
    </section>
  );
}
